import { useEffect, useState, type ReactElement } from "react";
import { Mail, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { mailHref, phone10, waHref } from "../contactLinks";
import { withOptOut } from "./fillTemplate";
import { useTemplates, type TemplateTarget } from "./useTemplateFor";
import { HelpTooltip } from "../help/HelpTooltip";

/**
 * Vista previa antes de enviar (PRD F.1): elige la plantilla, la muestra ya
 * rellena con los datos de la empresa (se puede retocar) y abre WhatsApp
 * (wa.me/52 + 10 dígitos, texto codificado) o el correo (mailto con asunto,
 * cuerpo y la línea de baja al final). Tú presionas enviar en tu app.
 */
export const SendPreview = ({
  channel,
  target,
  phone,
  email,
  onSent,
  trigger,
}: {
  channel: "whatsapp" | "email";
  target: TemplateTarget;
  phone?: string | null;
  email?: string | null;
  onSent?: () => void;
  trigger: ReactElement;
}) => {
  const { options, fill } = useTemplates();
  const [open, setOpen] = useState(false);
  const list = options(target, channel);
  const [tplId, setTplId] = useState<number | null>(null);
  const tpl = list.find((t) => t.id === tplId) ?? list[0];
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!open) return;
    const f = tpl ? fill(tpl, target) : null;
    setSubject(f?.subject ?? "");
    setBody(f?.body ?? withOptOut(channel, ""));
    // al abrir o al cambiar de plantilla
  }, [open, tpl?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const wa = channel === "whatsapp";
  // la baja se vuelve a asegurar por si se borró al retocar
  const text = withOptOut(channel, body.trim());
  const href = wa ? waHref(phone, text) : mailHref(email, subject, text);
  const to = wa ? (phone10(phone) ? `+52 ${phone10(phone)}` : null) : email;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="min-w-0 sm:max-w-lg [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-1">
            {wa ? "WhatsApp" : "Correo"} a {target.company_name ?? "—"}
            <HelpTooltip topic="templates.channel" />
          </DialogTitle>
          <DialogDescription>
            Para: {to ?? "sin dato de contacto"} · revisa el texto y presiona
            enviar en tu app.
          </DialogDescription>
        </DialogHeader>
        {list.length > 1 && (
          <select
            aria-label="Plantilla"
            className="h-9 w-full min-w-0 rounded-md border bg-card px-2 text-sm"
            value={tpl?.id}
            onChange={(e) => setTplId(Number(e.target.value))}
          >
            {list.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.pending_validation ? " (pendiente de validar)" : ""}
              </option>
            ))}
          </select>
        )}
        {tpl?.pending_validation && (
          <p className="rounded-md bg-[var(--th-2)]/20 px-2 py-1.5 text-xs">
            <b>Plantilla antigua sin revisar por el motor.</b> Revisa el texto
            antes de enviarlo.
          </p>
        )}
        {!tpl && (
          <p className="text-sm text-muted-foreground">
            No hay plantilla de este producto para este canal; escribe el
            mensaje.
          </p>
        )}
        {!wa && (
          <Input
            aria-label="Asunto"
            placeholder="Asunto"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        )}
        <Textarea
          aria-label="Mensaje"
          rows={wa ? 7 : 11}
          className="w-full min-w-0"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="inline-flex items-center gap-1">
          <Button asChild={!!href} disabled={!href} className="h-11">
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  setOpen(false);
                  onSent?.();
                }}
              >
                {wa ? <MessageCircle /> : <Mail />}
                {wa ? "Abrir WhatsApp" : "Abrir correo"}
              </a>
            ) : (
              <span>{wa ? "Sin teléfono" : "Sin correo"}</span>
            )}
          </Button>
          <HelpTooltip topic="templates.send" />
        </div>
      </DialogContent>
    </Dialog>
  );
};
